import type { Messages } from "@/i18n/locales/en";
import { legalMessagesSv } from "@/i18n/locales/legalSvData";
import { pagesMessagesSv } from "@/i18n/locales/pagesSvData";

export const svMessages = {
  meta: {
    description:
      "WasOK — skanna produkter, spara betyg och anteckningar, kom ihåg vad som var värt att köpa.",
    title: "WasOK — Kom ihåg vad som var värt att köpa",
  },
  header: {
    navFeatures: "Funktioner",
    navHowItWorks: "Så fungerar det",
    navPrivacy: "Integritet",
    navUseCases: "Användningsområden",
    download: "Ladda ner",
    openMenu: "Öppna meny",
    closeMenu: "Stäng meny",
    languageAria: "Språk",
  },
  hero: {
    badge: "📱 Nu tillgänglig på Android",
    title: "Kom ihåg vad som var värt att köpa.",
    subtitle:
      "Skanna produkter, spara betyg och anteckningar — och kom genast ihåg om en produkt faktiskt var bra.",
    googlePlay: "Ladda ner på Google Play",
    watchDemo: "Se demo",
    privacyLine: "All data stannar på din enhet.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skanna för att komma ihåg",
    snackCard: "Jättegott!",
    coffeeCard: "För bitter",
    scrollHint: "Scrolla för att utforska",
  },
  problems: {
    heading: "Känns det igen?",
    subheading:
      "Vi har alla varit där — framför hyllan och försökt minnas om vi gillade produkten förra gången.",
    notedAgo: "✓ Du antecknade detta för 3 månader sedan",
    items: [
      {
        question: "Vilken choklad tyckte jag egentligen om?",
        note: "Perfekt sötma",
      },
      {
        question: "Varför slutade jag köpa det här schampot?",
        note: "Torkade ut håret",
      },
      {
        question: "Var det här kaffet bra eller en besvikelse?",
        note: "För bitter",
      },
    ],
  },
  howItWorks: {
    heading: "Så fungerar det",
    subheading:
      "Fyra enkla steg så att du aldrig glömmer dina produktåsikter igen.",
    stepPrefix: "Steg",
    steps: [
      {
        title: "Skanna vilken produktstreckkod som helst",
        description:
          "Snabb och enkel skanning med kameran. Fungerar med alla streckkoder.",
      },
      {
        title: "Spara betyg, kommentarer och foton",
        description:
          "Betygsätt produkter, lägg till anteckningar, bifoga foton och logga priser och platser.",
      },
      {
        title: "Kom genast ihåg din upplevelse",
        description:
          "Skanna samma produkt senare och se ditt tidigare betyg och anteckningar.",
      },
      {
        title: "Bygg smarta inköpslistor",
        description:
          "Skapa inköpslistor från produkter du redan litar på och gillar.",
      },
    ],
  },
  privacy: {
    heading: "Din inköpshistorik förblir din.",
    body: "WasOK hjälper dig att komma ihåg produkter du har testat samtidigt som dina betyg, anteckningar, foton och inköpslistor lagras lokalt på din enhet.",
    trustBanner:
      "💚 Byggd kring din inköpshistorik — inte kring att samla in dina data.",
    bullets: [
      "Inget konto krävs",
      "Ingen molnsynk krävs",
      "Lokal lagring först",
      "Säkerhetskopior under din kontroll",
      "Valfri streckkodssökning för produktinformation.",
      "Annonsfinansierad med Google AdMob",
    ],
  },
  features: {
    heading: "Allt du behöver",
    subheading:
      "Kraftfulla funktioner som gör produktspårning enkelt och privat.",
    items: [
      {
        title: "Streckkodsskanner",
        description: "Snabb och exakt streckkodsskanning för alla produkter.",
      },
      {
        title: "Produktbetyg",
        description: "Betygsätt produkter med 1–5 stjärnor för att minnas kvaliteten.",
      },
      {
        title: "Produktfoton",
        description: "Bifoga foton för att minnas förpackning eller detaljer.",
      },
      {
        title: "Kategorier",
        description: "Organisera produkter i egna kategorier.",
      },
      {
        title: "Butiksspårning",
        description: "Kom ihåg var du köpte varje produkt.",
      },
      {
        title: "Prisloggning",
        description: "Spåra priser för att hitta de bästa erbjudandena.",
      },
      {
        title: "Inköpslistor",
        description: "Bygg listor från dina favoritprodukter.",
      },
      {
        title: "Offlineåtkomst",
        description: "Fungerar perfekt utan internetanslutning.",
      },
      {
        title: "Lokal lagring",
        description: "All din data lagras säkert på din enhet.",
      },
    ],
  },
  useCases: {
    heading: "Perfekt för varje hylla",
    subheading:
      "Oavsett om du handlar mat, skönhet eller hushållsartiklar — WasOK hjälper dig att komma ihåg.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & godis",
        description: "Kom ihåg vilka godsaker som faktiskt var bra.",
      },
      {
        emoji: "☕",
        title: "Kaffe & drycker",
        description: "Spåra kaffen som är värda att köpa igen.",
      },
      {
        emoji: "💄",
        title: "Kosmetik & hudvård",
        description: "Spara anteckningar om hudreaktioner.",
      },
      {
        emoji: "🧹",
        title: "Hushållsartiklar",
        description: "Undvik att köpa produkter som misslyckades igen.",
      },
    ],
  },
  screenshots: {
    heading: "Vackert & intuitivt",
    subheading:
      "Ett genomtänkt gränssnitt som gör produktspårning till ett nöje.",
    items: [
      { title: "Skanner" },
      { title: "Produktdetalj" },
      { title: "Betygsättning" },
      { title: "Sök & historik" },
      { title: "Inköpslista" },
    ],
  },
  cta: {
    heading: "Inköpsminne för verkliga livet.",
    body: "Sluta glömma vilka produkter som var värda att köpa. Börja handla smartare idag.",
    googlePlay: "Ladda ner på Google Play",
    scanQr: "Skanna för att ladda ner",
    tagline: "Privat · Offline först · Enkelt",
  },
  footer: {
    tagline: "Kom ihåg vad som var värt att köpa.",
    linksHeading: "Länkar",
    privacyPolicy: "Integritetspolicy",
    terms: "Användarvillkor",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Öppen källkodslicenser",
    linkAdvertising: "Annonsering & monetisering",
    downloadHeading: "Ladda ner",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Alla rättigheter förbehållna. Gjort med 💚 för smartare shopping.",
  },
  legal: legalMessagesSv,
  faq: pagesMessagesSv.faq,
  support: pagesMessagesSv.support,
  advertising: pagesMessagesSv.advertising,
} satisfies Messages;
